import { useState } from 'react'
import { Megaphone, RefreshCw, Send, Clock, XCircle, Eye, History, Languages } from 'lucide-react'
import { getBroadcastInfo, adminSendBroadcast, adminCancelBroadcast, adminTranslate } from '../../services/api'
import { BROADCAST_TEMPLATES_BY_GROUP } from '../../constants/broadcastTemplates'
import {
  C, Card, Row, Btn, Chips, BigStat, Loading, ErrorLine, Empty,
  inputStyle, selectStyle, labelStyle, useAsync, fmtDateTime, Pill, divider, confirmAsync,
} from './adminUI'

// Раздел «Рассылка» (task.txt: «Расширить функционал рассылки»).
// Было: одно поле и кнопка «отправить всем». Стало:
//   • сегменты получателей с живыми счётчиками (кому реально уйдёт);
//   • отдельный текст для англоязычных юзеров (language='en');
//   • разметка HTML/Markdown (Telegram-теги <b>, <i>, ссылки);
//   • тестовая отправка только себе — посмотреть, как выглядит;
//   • отложенная отправка (диспетчер проверяет очередь на сервере);
//   • история рассылок с итогами и отменой отложенных.
//
// 2026-09-20 (замечания владельца) добавилось:
//   • ЗАГОТОВКИ — готовые тексты под типовые ситуации (перезапуск, работы,
//     нововведения, проверка после работ), сразу на двух языках. Клик
//     подставляет оба текста, рекомендованный сегмент и разметку;
//   • АВТОПЕРЕВОД RU→EN — английский можно не писать вовсе: сервер переведёт
//     русский текст сам (TG-BOT/src/translate.js) в момент отправки, а поле EN
//     остаётся редактируемым. Если перевод не удался — англоязычные получат
//     русский текст, и админ увидит причину в результате отправки.
//
// Ключи сегментов приходят с бэкенда (broadcastSegments) — один источник,
// чтобы счётчики и фактическая выборка получателей не расходились.

const SEGMENT_LABELS = {
  all:        'Все активные',
  subscribed: 'С подпиской',
  nosub:      'Без подписки',
  expired:    'Подписка истекла',
  expiring:   'Истекает за 3 дня',
  withbots:   'С ботами',
  nobots:     'Без ботов',
  withkeys:   'С API-ключами',
  nokeys:     'Без ключей',
  inactive7d: 'Не заходили 7 дней',
  new7d:      'Новые за 7 дней',
  ru:         'Русский язык',
  en:         'English',
}

const STATUS = {
  scheduled: { label: 'отложена', color: C.indigo },
  sending:   { label: 'отправляется', color: C.warn },
  done:      { label: 'готово', color: C.accent },
  failed:    { label: 'ошибка', color: C.danger },
  cancelled: { label: 'отменена', color: C.dim },
}

// datetime-local отдаёт «2026-08-18T21:30» в местном времени браузера.
// Бэкенд ждёт ISO — переводим через Date, чтобы пояс не сбился.
const toIso = (local) => {
  if (!local) return null
  const ts = Date.parse(local)
  return Number.isFinite(ts) ? new Date(ts).toISOString() : null
}

// Хвост к результату отправки: что стало с английским текстом. Сервер отдаёт
// translation = { translated, provider, warn, error }:
//   provider 'manual' — английский взяли из поля EN (правка человека);
//   provider 'google'|'mymemory' — перевёл сервер;
//   translated:false — перевода нет, англоязычные получат русский текст.
const translationNote = (r) => {
  const t = r?.translation
  if (!t) return ''
  if (t.translated) {
    return t.provider === 'manual'
      ? ' Англоязычным уйдёт текст из поля EN.'
      : ` Английский переведён автоматически (${t.provider})${t.warn ? ' — проверьте разметку' : ''}.`
  }
  return ` Английский получить не удалось (${t.error || 'причина неизвестна'}) — англоязычные получат русский текст.`
}

export default function BroadcastSection() {
  const { data, loading, error, reload } = useAsync(() => getBroadcastInfo(), [])

  const [segment, setSegment] = useState('all')
  const [message, setMessage] = useState('')
  const [messageEn, setMessageEn] = useState('')
  const [parseMode, setParseMode] = useState('')
  const [when, setWhen] = useState('')
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState('')
  // Заготовка, применённая последней — чтобы показать, какой сегмент подставился.
  const [applied, setApplied] = useState(null)
  // Откуда взялся английский текст: '' | 'template' | 'translate' | 'manual'.
  // Нужно только для подписи под полем EN (правка руками — это уже не перевод).
  const [enSource, setEnSource] = useState('')
  // Состояние кнопки «Перевести на английский»: { busy } | { ok, provider, warn } | { error }
  const [tr, setTr] = useState(null)

  const counts = data?.counts || {}
  const segments = data?.segments || Object.keys(SEGMENT_LABELS)
  const history = data?.history || []
  const total = counts[segment] ?? 0

  // Клик по заготовке: подставляем ОБА текста (у заготовок перевод написан
  // человеком, машинный тут не нужен), рекомендованный сегмент и разметку.
  // Дальше админ правит текст как обычно — поля обычные, ничего не блокируется.
  const applyTemplate = (t) => {
    setMessage(t.ru)
    setMessageEn(t.en || '')
    setEnSource(t.en ? 'template' : '')
    setSegment(t.segment || 'all')
    setParseMode(t.parseMode || '')
    setWhen('')
    setNote('')
    setTr(null)
    setApplied(t)
  }

  // Кнопка «Перевести на английский»: переводит текущий русский текст и кладёт
  // результат в поле EN (предзаполнено, но редактируемо). Неудача перевода —
  // не ошибка отправки: показываем причину, поле остаётся как есть.
  // Возвращает переведённый текст либо null — этим пользуется «Проверить EN».
  const translate = async () => {
    const text = message.trim()
    if (!text || tr?.busy) return null
    setTr({ busy: true })
    try {
      const r = await adminTranslate(text, parseMode || undefined)
      if (r?.ok && r.text) {
        setMessageEn(r.text)
        setEnSource('translate')
        setTr({ ok: true, provider: r.provider, warn: r.warn })
        return r.text
      }
      setTr({ error: r?.error || 'перевод не удался' })
      return null
    } catch (e) {
      setTr({ error: e?.message || String(e) })
      return null
    }
  }

  // «Проверить EN»: присылаем себе английский вариант — ровно то, что получат
  // англоязычные. Если английского текста ещё нет, сначала переводим (то же
  // самое сделал бы сервер при настоящей отправке). Текст передаётся в send
  // аргументом: setState асинхронный, и в замыкании send поле EN было бы старым.
  const testEn = async () => {
    if (busy) return
    let en = messageEn.trim()
    if (!en) {
      en = await translate()
      if (!en) return // причина уже показана подписью под полем EN
    }
    send('testEn', en)
  }

  const send = async (mode, enOverride) => {
    const text = message.trim()
    if (!text) return

    if (mode === 'real') {
      const at = when ? ` на ${when.replace('T', ' ')}` : ''
      const ok = await confirmAsync(
        `Отправить${at}?\n\nСегмент: ${SEGMENT_LABELS[segment] || segment}\nПолучателей: ${total}`,
      )
      if (!ok) return
    }

    setBusy(mode); setNote('')
    // 'test'  — тест русским текстом, 'testEn' — тест английским (то, что
    // получат англоязычные), 'real' — настоящая отправка или отложка.
    const isTest = mode === 'test' || mode === 'testEn'
    const enText = (enOverride ?? messageEn).trim()
    try {
      const r = await adminSendBroadcast({
        // messageEn уходит, только если админ его видит в поле (заготовка,
        // перевод или ручная правка). Пустое поле = «переведи сам» — сервер
        // переведёт message и вернёт translation в ответе.
        message,
        messageEn: enText || undefined,
        segment,
        parseMode: parseMode || undefined,
        scheduledAt: mode === 'real' && when ? toIso(when) : undefined,
        testOnly: isTest || undefined,
        testLang: mode === 'testEn' ? 'en' : undefined,
      })

      if (isTest) {
        const lang = mode === 'testEn' ? 'английским текстом' : 'русским текстом'
        setNote((r?.sent
          ? `Тест отправлен вам в чат ${lang}`
          : 'Тест не доставлен: бот запущен без BOT_TOKEN или вы не писали ему /start') + translationNote(r))
      } else if (r?.scheduled) {
        setNote(`Отложена на ${String(r.scheduledAt).replace('T', ' ')} — получателей ${r.total}.` + translationNote(r))
        setWhen('')
        setMessage(''); setMessageEn(''); setEnSource(''); setApplied(null)
      } else if (!r?.total) {
        setNote('В этом сегменте нет получателей — ничего не отправлено.' + translationNote(r))
      } else {
        setNote(`Рассылка #${r.id} запущена: получателей ${r.total}. Итог появится в истории.` + translationNote(r))
        setMessage(''); setMessageEn(''); setEnSource(''); setApplied(null)
      }
      reload()
    } catch (e) {
      setNote(`Ошибка: ${e?.message || e}`)
    } finally {
      setBusy('')
    }
  }

  const cancel = async (id) => {
    if (!(await confirmAsync(`Отменить отложенную рассылку #${id}?`))) return
    setBusy(`cancel${id}`); setNote('')
    try {
      await adminCancelBroadcast(id)
      setNote(`Рассылка #${id} отменена`)
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy('') }
  }

  return (
    <div>
      <Card
        title="Новая рассылка"
        icon={Megaphone}
        accent={C.warn}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>}
      >
        <ErrorLine onRetry={reload}>{error}</ErrorLine>

        <div style={labelStyle}>Кому</div>
        <Chips
          options={segments.map(s => ({ key: s, label: SEGMENT_LABELS[s] || s, count: counts[s] ?? 0 }))}
          value={segment}
          onChange={setSegment}
        />

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <BigStat
            label="Получателей"
            value={loading ? '…' : total}
            color={total ? C.accent : C.dim}
          />
          <BigStat label="Всего активных" value={loading ? '…' : (counts.all ?? 0)} color="#fff" />
        </div>

        <div style={labelStyle}>Заготовки</div>
        <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.45, marginBottom: 8 }}>
          Готовые тексты под типовые ситуации. Нажмите — русский и английский
          тексты подставятся в поля вместе с рекомендованным сегментом и разметкой;
          дальше правьте как обычно.
        </div>
        {BROADCAST_TEMPLATES_BY_GROUP.map(g => (
          <div key={g.group} style={{ marginBottom: 10 }}>
            <div style={{
              fontSize: 10.5, color: C.muted, fontWeight: 700,
              marginBottom: 5, letterSpacing: 0.3,
            }}>
              {g.group}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {g.items.map(t => (
                <Btn
                  key={t.id}
                  small
                  ghost
                  color={applied?.id === t.id ? C.accent : C.muted}
                  onClick={() => applyTemplate(t)}
                >
                  {t.title}
                </Btn>
              ))}
            </div>
          </div>
        ))}
        {applied && (
          <div style={{
            fontSize: 10.5, color: C.dim, lineHeight: 1.5, marginBottom: 12,
            background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '7px 9px',
          }}>
            <span style={{ color: C.accent }}>Заготовка: {applied.title}.</span>{' '}
            Сегмент: <span style={{ color: '#fff', fontWeight: 700 }}>
              {SEGMENT_LABELS[applied.segment] || applied.segment}
            </span> — можно изменить в «Кому». {applied.hint}
            {applied.when ? ` ${applied.when}` : ''}
          </div>
        )}

        <div style={labelStyle}>Текст (русский)</div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={5}
          maxLength={4096}
          placeholder="Что отправить пользователям…"
          style={{ ...inputStyle, fontFamily: 'inherit', fontSize: 14, resize: 'vertical', lineHeight: 1.5 }}
        />
        <div style={{ fontSize: 10, color: C.dim, textAlign: 'right', marginTop: 4 }}>{message.length}/4096</div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginTop: 10, marginBottom: 6, flexWrap: 'wrap',
        }}>
          <div style={{ ...labelStyle, marginBottom: 0, flex: 1, minWidth: 0 }}>
            Текст для англоязычных (заполняется автоматически)
          </div>
          <Btn
            small
            ghost
            icon={Languages}
            color={C.indigo}
            disabled={!message.trim() || !!tr?.busy}
            onClick={translate}
          >
            {tr?.busy ? 'Перевожу…' : 'Перевести на английский'}
          </Btn>
        </div>
        <textarea
          value={messageEn}
          onChange={e => { setMessageEn(e.target.value); setEnSource('manual'); setTr(null) }}
          rows={3}
          maxLength={4096}
          placeholder="Пусто — сервер переведёт русский текст сам при отправке"
          style={{ ...inputStyle, fontFamily: 'inherit', fontSize: 14, resize: 'vertical', lineHeight: 1.5 }}
        />
        <div style={{ fontSize: 10, color: C.dim, marginTop: 4, lineHeight: 1.45 }}>
          {tr?.ok && !tr.busy && (
            <span style={{ color: C.accent }}>
              Переведено ({tr.provider}){tr.warn ? ' — проверьте, не сломалась ли разметка' : ''}. Можно поправить вручную.
            </span>
          )}
          {tr?.error && !tr.busy && (
            <span style={{ color: C.danger }}>
              Перевод не удался: {tr.error}. Можно вписать английский вручную.
            </span>
          )}
          {!tr?.ok && !tr?.error && (
            <>
              {enSource === 'template' && 'Английский текст из заготовки. '}
              {enSource === 'manual' && 'Английский вписан вручную. '}
              Машинный перевод (Google → MyMemory) включается сам, когда поле пустое.
              Если перевести не получится — англоязычные получат русский текст, рассылка не сорвётся.
              Перед отправкой проверьте перевод кнопкой «Проверить EN».
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={labelStyle}>Разметка</div>
            <select value={parseMode} onChange={e => setParseMode(e.target.value)} style={selectStyle}>
              <option value="">Обычный текст</option>
              <option value="HTML">HTML</option>
              <option value="Markdown">Markdown</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={labelStyle}>Отправить позже</div>
            <input
              type="datetime-local"
              value={when}
              onChange={e => setWhen(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ fontSize: 10, color: C.dim, marginTop: 6, lineHeight: 1.45 }}>
          Разметка HTML: &lt;b&gt;жирный&lt;/b&gt;, &lt;i&gt;курсив&lt;/i&gt;, &lt;a href="…"&gt;ссылка&lt;/a&gt;.
          Незакрытый тег — и Telegram отклонит сообщение, поэтому сначала проверьте
          кнопкой «Проверить на себе». Время отложки — по часам вашего устройства.
        </div>

        <div style={divider} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn
            icon={when ? Clock : Send}
            color={C.warn}
            disabled={!!busy || !message.trim()}
            onClick={() => send('real')}
          >
            {busy === 'real' ? 'Отправляю…' : when ? 'Отложить' : `Отправить (${total})`}
          </Btn>
          <Btn
            ghost
            icon={Eye}
            color={C.indigo}
            disabled={!!busy || !message.trim()}
            onClick={() => send('test')}
          >
            {busy === 'test' ? '…' : 'Проверить на себе'}
          </Btn>
          <Btn
            ghost
            icon={Languages}
            color={C.muted}
            disabled={!!busy || !message.trim()}
            onClick={testEn}
          >
            {busy === 'testEn' ? '…' : 'Проверить EN'}
          </Btn>
        </div>
        <div style={{ fontSize: 10, color: C.dim, marginTop: 6, lineHeight: 1.45 }}>
          «Проверить EN» присылает вам английский вариант — то есть ровно то, что
          получат англоязычные. Если поля EN ещё нет, оно сначала заполнится
          переводом.
        </div>

        {note && (
          <div style={{
            fontSize: 12, marginTop: 10, lineHeight: 1.45,
            color: note.startsWith('Ошибка') ? C.danger : C.accent,
          }}>
            {note}
          </div>
        )}

        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
          Сообщение приходит каждому в чат с ботом. Отправка идёт на сервере
          в фоне (≈20 сообщений в секунду), поэтому большая рассылка займёт время —
          следите за итогом в истории ниже. Юзеры, заблокировавшие бота, попадают
          в «не дошло» и помечаются неактивными.
        </div>
      </Card>

      <Card title="История" icon={History} accent={C.muted}>
        {loading && !history.length ? <Loading /> : !history.length ? <Empty>Рассылок ещё не было</Empty> : history.map((h, i) => {
          const st = STATUS[h.status] || { label: h.status, color: C.muted }
          return (
            <div key={h.id} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fff', flex: 1, minWidth: 0 }}>
                  #{h.id} · {SEGMENT_LABELS[h.segment] || h.segment}
                </div>
                <Pill color={st.color}>{st.label}</Pill>
              </div>
              <div style={{
                fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginBottom: 6,
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {h.message}
              </div>
              <Row
                label="Дошло / всего"
                value={`${h.sent ?? 0} / ${h.total ?? 0}`}
                valueColor={h.failed ? C.warn : C.accent}
                valueSub={h.failed ? `не дошло ${h.failed}` : undefined}
              />
              <Row label="Создана" value={fmtDateTime(h.created_at)} />
              {h.scheduled_at && <Row label="Отправка" value={fmtDateTime(h.scheduled_at)} valueColor={C.indigo} />}
              {h.finished_at && <Row label="Завершена" value={fmtDateTime(h.finished_at)} />}
              {h.parse_mode && <Row label="Разметка" value={h.parse_mode} mono />}
              {/* message_en в истории: с 2026-09-20 английский может быть
                  переведён сервером автоматически. Нет текста — значит
                  англоязычные получили русский (перевод не удался). */}
              <Row
                label="Английский текст"
                value={h.message_en ? 'есть' : 'нет — ушёл русский'}
                valueColor={h.message_en ? C.accent : C.dim}
              />
              {h.status === 'scheduled' && (
                <div style={{ marginTop: 8 }}>
                  <Btn
                    small ghost color={C.danger} icon={XCircle}
                    disabled={busy === `cancel${h.id}`}
                    onClick={() => cancel(h.id)}
                  >
                    {busy === `cancel${h.id}` ? '…' : 'Отменить'}
                  </Btn>
                </div>
              )}
            </div>
          )
        })}
      </Card>
    </div>
  )
}
